import { Component, Input, OnInit, OnChanges, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

interface Service {
  type: string;
  name: string;
  count: number;
  icon: string;
  color: string;
  isRent?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-tickets-services',
  templateUrl: './tickets-services.component.html',
  styleUrls: ['./tickets-services.component.scss'],
})
export class TicketsServicesComponent implements OnInit, OnChanges {
  @Input() services: any;
  @Input() ticket: any;
  @Input() maxRows: number = 2; // Максимальное количество строк
  @Input() itemsPerRow: number = 1; // Один элемент в строке
  // Группировать одинаковые элементы (для карточек) или выводить полный список (для детального экрана)
  @Input() grouped: boolean = true;

  processedServices: Service[] = [];
  displayedServices: Service[] = [];
  hasMore: boolean = false;

  constructor(private modalService: NgbModal) {}

  ngOnInit() {
    this.processServices();
  }

  ngOnChanges() {
    this.processServices();
  }

  private processServices() {
    this.processedServices = [];

    // Если нет данных для обработки, выходим
    if (!this.services && !this.ticket) {
      this.calculateDisplayedServices();
      return;
    }

    // Обрабатываем services_details (новый формат из API)
    const otherServices: Service[] = [];
    const routerServices: any[] = [];
    const tveServices: any[] = [];
    const equipHirePurchaseServices: any[] = [];

    if (this.ticket && this.ticket.services_details && Array.isArray(this.ticket.services_details)) {
      this.ticket.services_details.forEach((service: any) => {
        const isEquipScale = service.type === 'EQUIP_SCALE';
        const serviceName: string = service.name || '';
        const isYtvService = service.type === 'YTV';

        if (['ENT', 'TVE'].includes(serviceName)) {
          return;
        }

        const isRouterType =
          ['W_NONSTOP', 'W_STOPPABLE_RENT', 'W_STOPPABLE_RENT_NR', 'W_NONSTOP_NR'].includes(service.type) ||
          (isEquipScale && (serviceName === 'Роутер' || serviceName === 'Роутер 4G'));

        const isTveType = service.type === 'TVE_RENT' || (isEquipScale && serviceName === 'ТВ-приставка');

        if (isRouterType) {
          routerServices.push(service);
          return;
        }

        if (isTveType) {
          tveServices.push(service);
          return;
        }

        if (service.type === 'EQUIP_HIRE_PURCHASE') {
          equipHirePurchaseServices.push(service);
          return;
        }

        // Остальные сервисы добавляем как есть
        otherServices.push({
          type: service.type,
          name: service.name,
          count: 1,
          icon: isYtvService ? 'yandex' : this.getServiceIcon(service.type, service.ownership_type),
          color: isYtvService ? 'gray-dark' : this.getServiceColorBy(service.type, service.ownership_type),
          isRent: !service.ownership_type || !['I', 'O'].includes(service.ownership_type),
        });
      });
    }

    // Обрабатываем старый формат services (исключаем tve_count и wifi_count)
    if (this.services) {
      // IPTV (не TVE/WiFi)
      if (this.services.iptv_count) {
        this.processedServices.push({
          type: 'iptv',
          name: 'Приставка IPTV',
          count: this.services.iptv_count,
          icon: 'iptv',
          color: 'gray-dark',
        });
      }

      // SIM (не TVE/WiFi)
      if (this.services.sim_count) {
        this.processedServices.push({
          type: 'sim',
          name: 'SIM',
          count: this.services.sim_count,
          icon: 'sim',
          color: 'gray-dark',
        });
      }

      // VPDN (не TVE/WiFi)
      if (this.services.vpdn_count) {
        this.processedServices.push({
          type: 'vpdn',
          name: 'Гигабитное подключение',
          count: this.services.vpdn_count,
          icon: 'giga',
          color: 'gray-dark',
        });
      }

      // Настройка ТВ (не TVE/WiFi)
      if (this.services.setting_tv) {
        this.processedServices.push({
          type: 'setting_tv',
          name: 'Настройка ТВ при переезде',
          count: 1,
          icon: 'tv',
          color: 'gray-dark',
        });
      }
    }

    // Сортируем и добавляем сначала роутеры, затем TVE
    // Группируем EQUIP_SCALE по названию и показываем количество
    const addGrouped = (arr: any[], iconOrFn: string | ((type: string, ownershipType: string) => string)) => {
      const map = new Map<string, { name: string; count: number; type: string; ownership_type: string }>();
      arr.forEach((s: any) => {
        // Важно: не схлопывать разные услуги только по type (например, разные W_NONSTOP).
        // Для EQUIP_SCALE группируем по имени, для остальных — по type + priority + name + ownership_type.
        const key =
          s.type === 'EQUIP_SCALE'
            ? `EQUIP_SCALE::${s.name || 'UNKNOWN'}::${s.ownership_type || ''}`
            : `${s.type}::${s.priority || ''}::${s.name || ''}::${s.ownership_type || ''}`;
        const existing = map.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(key, {
            name: s.name,
            count: 1,
            type: s.type,
            ownership_type: s.ownership_type,
          });
        }
      });

      Array.from(map.values()).forEach((item) => {
        const icon = typeof iconOrFn === 'function' ? iconOrFn(item.type, item.ownership_type) : iconOrFn;
        this.processedServices.push({
          type: item.type,
          name: item.name,
          count: item.count,
          icon,
          color: this.getServiceColorBy(item.type, item.ownership_type),
          isRent: !(item.ownership_type && ['I', 'O'].includes(item.ownership_type)),
        });
      });
    };

    const addPlain = (arr: any[], iconOrFn: string | ((type: string, ownershipType: string) => string)) => {
      arr.forEach((s: any) => {
        const icon = typeof iconOrFn === 'function' ? iconOrFn(s.type, s.ownership_type) : iconOrFn;
        this.processedServices.push({
          type: s.type,
          name: s.name,
          count: 1,
          icon,
          color: this.getServiceColorBy(s.type, s.ownership_type),
          isRent: !(s.ownership_type && ['I', 'O'].includes(s.ownership_type)),
        });
      });
    };

    // Роутеры (включая EQUIP_SCALE с именем 'Роутер'/'Роутер 4G')
    if (this.grouped) {
      addGrouped(routerServices, 'router');
    } else {
      addPlain(routerServices, 'router');
    }

    // TVE (включая EQUIP_SCALE 'ТВ-приставка') c сортировкой по priority для TVE_RENT
    tveServices.sort((a: any, b: any) => {
      const aIsTveRent = a.type === 'TVE_RENT';
      const bIsTveRent = b.type === 'TVE_RENT';
      if (aIsTveRent && bIsTveRent) {
        const priorityOrder = ['P', '1', 'S', '2', null];
        const aPriorityIndex = priorityOrder.indexOf(a.priority);
        const bPriorityIndex = priorityOrder.indexOf(b.priority);
        return aPriorityIndex - bPriorityIndex;
      }
      return 0;
    });
    // Функция для определения иконки TVE по ownership_type
    const getTveIcon = (type: string, ownershipType: string): string => {
      // ownership_type in 'I','O' = красный (red_tv) - продажа
      // остальные = чёрный (black_tv) - аренда
      // Для EQUIP_SCALE 'ТВ-приставка' = чёрный (black_tv)
      if (type === 'EQUIP_SCALE') {
        return 'black_tv';
      }
      if (ownershipType && ['I', 'O'].includes(ownershipType)) {
        return 'red_tv';
      }
      return 'black_tv';
    };
    if (this.grouped) {
      addGrouped(tveServices, getTveIcon);
    } else {
      addPlain(tveServices, getTveIcon);
    }

    // EQUIP_HIRE_PURCHASE (прочее оборудование)
    if (this.grouped) {
      addGrouped(equipHirePurchaseServices, 'other');
    } else {
      addPlain(equipHirePurchaseServices, 'other');
    }

    // Прочие сервисы в конце
    otherServices.forEach((s) => this.processedServices.push(s));

    // Определяем, какие сервисы показывать
    this.calculateDisplayedServices();
  }

  private calculateDisplayedServices() {
    const maxItems = this.maxRows * this.itemsPerRow;

    if (this.processedServices.length <= maxItems) {
      this.displayedServices = this.processedServices;
      this.hasMore = false;
    } else {
      // Оставляем место для кнопки "ещё"
      this.displayedServices = this.processedServices.slice(0, maxItems - 1);
      this.hasMore = true;
    }
  }

  private getServiceIcon(type: string, ownershipType?: string): string {
    if (type === 'TVE_RENT') {
      // ownership_type in 'I','O' = красный (red_tv) - продажа
      // остальные = чёрный (black_tv) - аренда
      if (ownershipType && ['I', 'O'].includes(ownershipType)) {
        return 'red_tv';
      }
      return 'black_tv';
    }

    if (['W_NONSTOP', 'W_STOPPABLE_RENT', 'W_STOPPABLE_RENT_NR', 'W_NONSTOP_NR'].includes(type)) {
      return 'router';
    }

    if (type === 'IS_PRESET') {
      return 'conv';
    }

    if (type === 'IS_INAC_PRESET') {
      return 'technology_communication';
    }

    if (type === 'EQUIP_HIRE_PURCHASE') {
      return 'other';
    }

    // Fallback для других типов
    const iconMap: { [key: string]: string } = {
      iptv: 'iptv',
      sim: 'sim',
      vpdn: 'giga',
      setting_tv: 'tv',
    };

    return iconMap[type] || 'settings';
  }

  private getServiceColorBy(type: string, ownershipType: string): string {
    // Согласно ТЗ "СА - Разделить цветовую индикацию в МП и WEB оборудования в рассрочку и продажу":
    // Продажа (EQUIP_SCALE) = красная иконка
    // Рассрочка (EQUIP_HIRE_PURCHASE; W_* с ownership_type = 'I') = синяя иконка
    // Аренда (W_* с ownership_type <> 'I' или без него; TVE_RENT) = чёрная иконка
    if (type === 'EQUIP_SCALE') {
      return 'red';
    }
    if (type === 'EQUIP_HIRE_PURCHASE') {
      return 'blue';
    }
    if (['W_NONSTOP', 'W_STOPPABLE_RENT', 'W_STOPPABLE_RENT_NR', 'W_NONSTOP_NR'].includes(type)) {
      return ownershipType === 'I' ? 'blue' : 'black';
    }
    if (type === 'TVE_RENT') {
      return ownershipType && ['I', 'O'].includes(ownershipType) ? 'red' : 'black';
    }
    return !ownershipType || !['I', 'O'].includes(ownershipType) ? 'purple' : 'red';
  }

  showAllServices() {
    // Открываем модальное окно со всеми сервисами
    const modalRef = this.modalService.open(ServicesModalComponent, {
      windowClass: 'modal-wrapper modal-wrapper--w-600',
      backdrop: true, // Предотвращаем стандартное закрытие по клику на backdrop
      keyboard: true, // Включаем закрытие по ESC
    });

    modalRef.componentInstance.services = this.processedServices;
    modalRef.componentInstance.ticket = this.ticket;

    // Добавляем обработчик клика на modal-dialog (но не на modal-content)
    setTimeout(() => {
      const modalDialogElement = document.querySelector('.modal-dialog');
      if (modalDialogElement) {
        modalDialogElement.addEventListener('click', (event: Event) => {
          // Проверяем, что клик был именно на modal-dialog, а не на modal-content
          if (event.target === modalDialogElement) {
            modalRef.dismiss();
          }
        });
      }
    });
  }
}

// Компонент модального окна
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-services-modal',
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="services-modal-unique-wrapper">
      <div class="modal__header">
        <div class="modal-close color-gray-dark" (click)="activeModal.dismiss()"></div>
        <span class="title">Подключаемые сервисы</span>
      </div>
      <div class="modal__body">
        <div class="services-list">
          <div class="service-item" *ngFor="let service of services">
            <div
              class="service-icon"
              [style.color]="
                service.color === 'red'
                  ? '#ff2626'
                  : service.color === 'purple' || service.color === 'blue'
                  ? '#3b2bff'
                  : service.color === 'black'
                  ? '#282828'
                  : ''
              "
            >
              <app-icon [glyph]="service.icon" [size]="'24'" [color]="service.color"></app-icon>
            </div>
            <div class="service-details">
              <div class="service-name">{{ service.name }}</div>
              <div class="service-count" *ngIf="service.count > 1">Количество: {{ service.count }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal__footer">
        <div class="modal__footer-action">
          <button class="button" type="button" (click)="activeModal.dismiss()">Закрыть</button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .services-modal-unique-wrapper .title {
        font-size: 18px;
        font-weight: 600;
        color: #333333;
        text-align: center;
        display: block;
        width: 100%;
      }

      .services-modal-unique-wrapper .services-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .services-modal-unique-wrapper .service-item {
        display: flex;
        align-items: flex-start;
        padding: 20px;
        background-color: #ffffff;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        transition: box-shadow 0.2s ease;
      }

      .services-modal-unique-wrapper .service-item:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      .services-modal-unique-wrapper .service-icon {
        margin-right: 16px;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .services-modal-unique-wrapper .service-details {
        flex-grow: 1;
      }

      .services-modal-unique-wrapper .service-name {
        font-weight: 500;
        font-size: 16px;
        color: #333333;
        line-height: 1.4;
        margin-bottom: 4px;
      }

      .services-modal-unique-wrapper .service-count {
        color: #666666;
        font-size: 14px;
        font-style: italic;
      }

      /* Принудительное окрашивание иконок только в модальном окне */
      .services-modal-unique-wrapper .service-icon app-icon svg,
      .services-modal-unique-wrapper .service-icon app-icon svg path,
      .services-modal-unique-wrapper .service-icon app-icon svg g,
      .services-modal-unique-wrapper .service-icon app-icon svg circle,
      .services-modal-unique-wrapper .service-icon app-icon svg rect,
      .services-modal-unique-wrapper .service-icon app-icon svg polygon,
      .services-modal-unique-wrapper .service-icon app-icon svg ellipse,
      .services-modal-unique-wrapper .service-icon app-icon svg line {
        stroke: currentColor !important;
      }

      .services-modal-unique-wrapper .service-icon app-icon svg path[fill]:not([fill='none']),
      .services-modal-unique-wrapper .service-icon app-icon svg circle[fill]:not([fill='none']),
      .services-modal-unique-wrapper .service-icon app-icon svg rect[fill]:not([fill='none']),
      .services-modal-unique-wrapper .service-icon app-icon svg polygon[fill]:not([fill='none']) {
        fill: currentColor !important;
      }

      .services-modal-unique-wrapper .service-icon .icon--color-red,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red {
        color: #ff2626 !important;
      }

      .services-modal-unique-wrapper .service-icon .icon--color-red svg,
      .services-modal-unique-wrapper .service-icon .icon--color-red path,
      .services-modal-unique-wrapper .service-icon .icon--color-red circle,
      .services-modal-unique-wrapper .service-icon .icon--color-red ellipse,
      .services-modal-unique-wrapper .service-icon .icon--color-red line,
      .services-modal-unique-wrapper .service-icon .icon--color-red rect,
      .services-modal-unique-wrapper .service-icon .icon--color-red polygon,
      .services-modal-unique-wrapper .service-icon .icon--color-red g,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red svg,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red path,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red circle,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red ellipse,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red line,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red rect,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red polygon,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-red g {
        stroke: none !important;
        stroke-width: 0 !important;
        fill: #ff2626 !important;
      }

      .services-modal-unique-wrapper .service-icon .icon--color-purple svg,
      .services-modal-unique-wrapper .service-icon .icon--color-purple path,
      .services-modal-unique-wrapper .service-icon .icon--color-purple circle,
      .services-modal-unique-wrapper .service-icon .icon--color-purple ellipse,
      .services-modal-unique-wrapper .service-icon .icon--color-purple line,
      .services-modal-unique-wrapper .service-icon .icon--color-purple rect,
      .services-modal-unique-wrapper .service-icon .icon--color-purple polygon,
      .services-modal-unique-wrapper .service-icon .icon--color-purple g,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple svg,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple path,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple circle,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple ellipse,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple line,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple rect,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple polygon,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-purple g {
        stroke: none !important;
        stroke-width: 0 !important;
        fill: #3b2bff !important;
      }

      .services-modal-unique-wrapper .service-icon .icon--color-blue svg,
      .services-modal-unique-wrapper .service-icon .icon--color-blue path,
      .services-modal-unique-wrapper .service-icon .icon--color-blue circle,
      .services-modal-unique-wrapper .service-icon .icon--color-blue ellipse,
      .services-modal-unique-wrapper .service-icon .icon--color-blue line,
      .services-modal-unique-wrapper .service-icon .icon--color-blue rect,
      .services-modal-unique-wrapper .service-icon .icon--color-blue polygon,
      .services-modal-unique-wrapper .service-icon .icon--color-blue g,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue svg,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue path,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue circle,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue ellipse,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue line,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue rect,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue polygon,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-blue g {
        stroke: none !important;
        stroke-width: 0 !important;
        fill: #3b2bff !important;
      }

      .services-modal-unique-wrapper .service-icon .icon--color-black svg,
      .services-modal-unique-wrapper .service-icon .icon--color-black path,
      .services-modal-unique-wrapper .service-icon .icon--color-black circle,
      .services-modal-unique-wrapper .service-icon .icon--color-black ellipse,
      .services-modal-unique-wrapper .service-icon .icon--color-black line,
      .services-modal-unique-wrapper .service-icon .icon--color-black rect,
      .services-modal-unique-wrapper .service-icon .icon--color-black polygon,
      .services-modal-unique-wrapper .service-icon .icon--color-black g,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black svg,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black path,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black circle,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black ellipse,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black line,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black rect,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black polygon,
      .services-modal-unique-wrapper .service-icon app-icon.icon--color-black g {
        stroke: none !important;
        stroke-width: 0 !important;
        fill: #282828 !important;
      }
    `,
  ],
})
export class ServicesModalComponent {
  @Input() services: Service[] = [];
  @Input() ticket: any;

  constructor(public activeModal: NgbActiveModal) {}
}
